/**
 * Per-player view: one row per scope — every tracked season, then each league's
 * career, then the overall career.
 *
 * Same grid and same sections as the league table, transposed. Two percentile
 * modes: the ones the roll-up published ("vs league"), and — by fetching each
 * season's league population — a re-rank against whoever passes the current
 * filters. A single player's own rows never define a population; ranking him
 * against his own seasons would answer a different question than the chips do
 * everywhere else.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  Link as MuiLink,
  Popover,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack, OpenInNew } from "@mui/icons-material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-premium";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  PlayerStatsGarden,
  PlayerStatsScopeRow,
} from "../../../models/PlayerStats";
import { populationValues, rankRows, visibleColumns } from "./columns";
import {
  CUTOUT_FILTER,
  HEADSHOT_URL,
  OptionalImage,
} from "./PlayerIdentityCell";
import FilterPanel from "./FilterPanel";
import {
  activeFilters,
  decodeFilters,
  encodeFilters,
  filterableColumns,
  IDENTITY_FILTER_FIELDS,
} from "./filters";
import PlayerStatsGrid from "./PlayerStatsGrid";
import PositionChips from "./PositionChips";
import { usePlayerStatsContext, seasonLabel } from "./PlayerStatsLayout";
import { EfficiencyMode, PercentileMode } from "./PlayerStatsToolbar";
import SectionTabs, { useSelectedSection } from "./SectionTabs";
import {
  usePlayerStatsPlayer,
  usePlayerStatsPopulations,
} from "./usePlayerStats";
import { playerIdFromRoute } from "./playerIds";
import DevSourceLabel from "../../Elements/DevSourceLabel";

/** Career rows are a different kind of thing from a season, so they read as one. */
const CAREER_ROW_CLASS = "player-stats-career-row";

const POSITION_LABELS: Record<string, string> = {
  G: "Guard",
  W: "Wing",
  F: "Forward",
  C: "Center",
};

/**
 * Blitz player pages, e.g.
 *   https://blitz.trailblaze.rs/pro/players/1631101
 *   https://blitz.trailblaze.rs/draft/players/sr:player:3074271
 *
 * The two gardens differ in path as well as id — `pro` keyed on the NBA id,
 * `draft` on the Sportradar id — which is why the host is configured and the path
 * is built here. Unset host means no button, which beats a link that 404s.
 *
 * Sportradar ids carry colons and Blitz's URLs show them unescaped. Colons are
 * legal in a path segment (RFC 3986), so the id goes through as-is rather than
 * being percent-encoded into something that doesn't match their scheme.
 */
const BLITZ_PATHS: Record<PlayerStatsGarden, string> = {
  nba: "pro",
  prospect: "draft",
};

function blitzUrl(
  garden: PlayerStatsGarden,
  blitzId: string | null,
): string | null {
  const host = process.env.REACT_APP_BLITZ_URL;
  if (!host || !blitzId) return null;
  return `${host.replace(/\/$/, "")}/${BLITZ_PATHS[garden]}/players/${blitzId}`;
}

function isCareer(scope: PlayerStatsScopeRow): boolean {
  return (scope.scopeType || "").toLowerCase().includes("career");
}

function scopeLabel(scope: PlayerStatsScopeRow): string {
  if (!isCareer(scope)) return seasonLabel(scope.season);
  return scope.league ? `${scope.league} career` : "Career";
}

export default function PlayerSeasonTable() {
  const { garden, sections, loading: sectionsLoading } = usePlayerStatsContext();
  // Unlike the league table, this view doesn't refetch on a section change: the
  // player endpoint isn't section-scoped, because one player's rows are small
  // enough to send whole. Switching sub-tabs here is instant.
  const [section, setSection] = useSelectedSection(sections);
  const { playerId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, loading, error } = usePlayerStatsPlayer(
    garden,
    playerIdFromRoute(garden, playerId),
  );

  // Escape returns to the table, matching the labelled shortcut on the button.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") navigate(`..${window.location.search}`);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  const modeParam = params.get("mode");
  const percentileMode: PercentileMode =
    modeParam === "off" ? "off" : modeParam === "view" ? "view" : "published";

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const position = params.get("pos") ?? "ALL";
  const columnFilters = useMemo(
    () => decodeFilters(params.get("filter")),
    [params],
  );
  const populationFilters = useMemo(
    () => ({ position, columnFilters }),
    [position, columnFilters],
  );
  // Position isn't counted: it has its own chips in the header, where its state is
  // already visible. Matches the league table's badge.
  const filterCount = activeFilters(columnFilters).length;

  // Named in full in the footer, because a chip reading "C" on a forward's page is
  // ambiguous about which side of the comparison it applies to.
  const comparisonLabel = [
    position === "ALL"
      ? "the league"
      : `${POSITION_LABELS[position] ?? position}s`,
    filterCount ? "matching the filters" : null,
  ]
    .filter(Boolean)
    .join(" ");

  /**
   * Comparing against a position group only means anything in "vs current
   * filters" mode, so picking one turns that mode on. Otherwise the chip
   * highlights and nothing on screen moves, which reads as broken — and wanting
   * the comparison is the only reason to touch this control.
   *
   * Clearing back to ALL leaves the mode alone: column filters may still be
   * defining a population, and dropping out from under them would be worse than
   * doing nothing.
   */
  const setComparisonPosition = (next: string) => {
    const updates: Record<string, string | null> = {
      pos: next === "ALL" ? null : next,
    };
    if (next !== "ALL") updates.mode = "view";
    setParam(updates);
  };

  const efficiencyMode = (params.get("eff") as EfficiencyMode) || "ppp";
  const columns = useMemo(
    () => visibleColumns(section?.columns ?? [], efficiencyMode),
    [section, efficiencyMode],
  );
  const hasVariants = (section?.columns ?? []).some((c) => c.variant);
  const allScopes = useMemo(() => data?.scopes ?? [], [data]);

  const availableLeagues = useMemo(
    () =>
      Array.from(
        new Set(
          allScopes.map((s) => s.league).filter((l): l is string => !!l),
        ),
      ),
    [allScopes],
  );

  // Opens scoped to wherever you clicked in from — the NBA table lands on NBA,
  // the prospect table on whichever league it was showing — then you can widen
  // it. Falls back to everything the player has if that league isn't one of them.
  const cameFrom = params.get("league") ?? (garden === "nba" ? "NBA" : null);
  const requestedLeagues = (params.get("leagues") ?? "")
    .split(",")
    .filter((l) => availableLeagues.includes(l));
  const selectedLeagues = requestedLeagues.length
    ? requestedLeagues
    : cameFrom && availableLeagues.includes(cameFrom)
      ? [cameFrom]
      : availableLeagues;

  const showsEveryLeague = selectedLeagues.length === availableLeagues.length;

  const scopes = useMemo(
    () =>
      allScopes.filter((scope) => {
        // The overall career spans every league, so it only belongs on screen
        // when every league is. Narrowed down, its totals would include seasons
        // that aren't shown.
        if (!scope.league) return isCareer(scope) ? showsEveryLeague : true;
        return selectedLeagues.includes(scope.league);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allScopes, selectedLeagues.join(","), showsEveryLeague],
  );

  const sortModel: GridSortModel = useMemo(() => {
    const field = params.get("sort");
    // No sort by default: the backend's order (newest season first, careers
    // last) is the reading order, and any column sort would scramble it.
    if (!field) return [];
    return [{ field, sort: params.get("dir") === "asc" ? "asc" : "desc" }];
  }, [params]);

  const setParam = (updates: Record<string, string | null>) => {
    const updated = new URLSearchParams(params);
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === "") updated.delete(key);
      else updated.set(key, value);
    });
    setParams(updated, { replace: true });
  };

  // Season scopes only. A career row has no population to rank against — the wide
  // cache is keyed by integer season, and career scopes carry a null one — so
  // those keep their published chips in every mode.
  const rankableScopes = useMemo(
    () =>
      scopes.filter(
        (s): s is PlayerStatsScopeRow & { league: string; season: number } =>
          !isCareer(s) && !!s.league && s.season != null,
      ),
    [scopes],
  );

  const scopeKeys = useMemo(
    () => rankableScopes.map((s) => `${s.league}:${s.season}`),
    [rankableScopes],
  );

  const filterFields = useMemo(
    () =>
      columnFilters
        .filter((f) => !IDENTITY_FILTER_FIELDS.has(f.field))
        .map((f) => f.field),
    [columnFilters],
  );

  const { data: populations, loading: populationsLoading } =
    usePlayerStatsPopulations(
      garden,
      scopeKeys,
      section?.key ?? null,
      filterFields,
      percentileMode === "view",
    );

  const viewPercentiles = useMemo(() => {
    if (percentileMode !== "view" || !populations) return undefined;
    const perScope: Record<string, Record<string, number[]>> = {};
    Object.entries(populations.scopes).forEach(([key, population]) => {
      perScope[key] = populationValues(
        population,
        columns,
        garden,
        populationFilters,
      );
    });
    return rankRows(
      rankableScopes,
      columns,
      (row) => row.key,
      (column, row) => perScope[`${row.league}:${row.season}`]?.[column.key],
    );
  }, [
    percentileMode,
    populations,
    columns,
    garden,
    populationFilters,
    rankableScopes,
  ]);

  const filterColumns = useMemo(
    () => filterableColumns(sections, section?.key),
    [sections, section],
  );

  const identityColumns = useMemo(() => buildIdentityColumns(), []);

  const player = data?.player;
  const latest = scopes.find((s) => !isCareer(s));
  const meta = [
    player?.pos ? POSITION_LABELS[player.pos] ?? player.pos : null,
    latest?.team,
    latest?.league,
  ]
    .filter(Boolean)
    .join(" · ");

  const blitz = blitzUrl(garden, player?.blitzId ?? null);
  // NBA only: `blitzId` is an nbaId here and a Sportradar id for prospects, which
  // this CDN knows nothing about. `OptionalImage` also self-hides on a 404, so a
  // missing headshot collapses rather than leaving a gap.
  const headshot =
    garden === "nba" && player?.blitzId
      ? `${HEADSHOT_URL}/${player.blitzId}.png`
      : null;

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 0.85,
          backgroundColor: "#fff",
          border: "1px solid #dfe2e6",
          borderBottom: "none",
        }}
      >
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`..${window.location.search}`)}
          sx={{
            px: 1.5,
            backgroundColor: "#17191c",
            color: "#fff",
            borderRadius: 0,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.07em",
            "&:hover": { backgroundColor: "#000" },
          }}
        >
          Back to table
          <Box component="span" sx={{ ml: 1, color: "#8a8a8a", fontSize: 11 }}>
            esc
          </Box>
        </Button>
        <Box sx={{ width: "1px", height: 34, backgroundColor: "#e6e8eb" }} />
        {loading ? (
          <Skeleton variant="text" width={220} height={32} />
        ) : (
          <Stack direction="row" alignItems="flex-end" spacing={1.25}>
            {/* Same cut-out as the leaderboard's identity cell, reused rather than
                restated so the two can't drift. Sized to the name block's two lines;
                bottom-aligned because the source is a head-and-shoulders crop, so a
                shared baseline sits the figure on the text rather than floating it. */}
            <OptionalImage
              src={headshot}
              alt={player?.name ?? ""}
              width={38}
              height={44}
              style={{
                objectFit: "cover",
                objectPosition: "top",
                filter: CUTOUT_FILTER,
              }}
            />
            <Box>
              <Typography variant="h3" sx={{ fontSize: 25, lineHeight: 1.05 }}>
                {player?.name ?? "—"}
              </Typography>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}
              >
                {meta || "—"}
              </Typography>
            </Box>
          </Stack>
        )}
        <Box flexGrow={1} />
        {/* "Compare vs" rather than "Pos": on the league table this picks which
            players to show, here it picks who he's measured against. He stays on
            screen whichever group is selected — a forward can be ranked among
            centers, which is the whole point. */}
        <PositionChips
          value={position}
          onChange={setComparisonPosition}
          label="Compare vs"
        />
        {availableLeagues.length > 1 && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: "#8a8a8a",
                textTransform: "uppercase",
              }}
            >
              Leagues
            </Typography>
            <Select
              multiple
              size="small"
              value={selectedLeagues}
              onChange={(e) => {
                const next =
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value;
                // Clearing every league would show nothing; treat it as "all".
                setParam({ leagues: next.length ? next.join(",") : null });
              }}
              renderValue={(selected) =>
                selected.length === availableLeagues.length
                  ? "All leagues"
                  : selected.join(", ")
              }
              sx={{ borderRadius: 0, minWidth: 170, fontSize: 13 }}
            >
              {availableLeagues.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox
                    size="small"
                    checked={selectedLeagues.includes(option)}
                  />
                  <ListItemText
                    primary={option}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        {blitz && (
          <MuiLink
            href={blitz}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.9,
              border: "1px solid",
              borderColor: "primary.main",
              color: "primary.main",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
            }}
          >
            Open in Blitz
            <OpenInNew sx={{ fontSize: 14 }} />
          </MuiLink>
        )}
      </Stack>

      <SectionTabs
        sections={sections}
        section={section}
        onChange={setSection}
        loading={sectionsLoading}
      />

      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 0, mt: 0.5 } } }}
      >
        <FilterPanel
          columns={filterColumns}
          filters={columnFilters}
          onChange={(next) => setParam({ filter: encodeFilters(next) })}
        />
      </Popover>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <PlayerStatsGrid
          rows={scopes}
          columns={columns}
          identityColumns={identityColumns}
          getRowId={(row) => row.key}
          getRowClassName={(row) =>
            isCareer(row) ? CAREER_ROW_CLASS : undefined
          }
          loading={loading}
          sortModel={sortModel}
          onSortModelChange={(model) =>
            setParam({
              sort: model[0]?.field ?? null,
              dir: model[0]?.sort === "asc" ? "asc" : null,
            })
          }
          percentileMode={percentileMode}
          percentileModes={["published", "view", "off"]}
          onPercentileModeChange={(mode) =>
            setParam({ mode: mode === "published" ? null : mode })
          }
          viewPercentiles={viewPercentiles}
          onOpenFilters={setFilterAnchor}
          filtersOpen={!!filterAnchor}
          filterCount={filterCount}
          efficiencyMode={efficiencyMode}
          onEfficiencyModeChange={
            hasVariants
              ? (mode) => setParam({ eff: mode === "ppp" ? null : mode })
              : undefined
          }
          exportFileName={`${player?.name ?? "player"}-${section?.key ?? ""}`}
        />
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          px: 2,
          py: 0.85,
          backgroundColor: "#fff",
          border: "1px solid #dfe2e6",
          borderTop: "none",
        }}
      >
        <FooterText bold>Showing {scopes.length} rows</FooterText>
        {!showsEveryLeague && (
          <FooterText>{selectedLeagues.join(', ')} only</FooterText>
        )}
        <FooterText>
          {percentileMode === "off"
            ? "Percentiles hidden"
            : percentileMode === "view"
              ? populationsLoading
                ? "Loading league populations…"
                : `Ranked season by season against ${comparisonLabel}`
              : "Percentiles as published by the roll-up"}
        </FooterText>
        {percentileMode === "view" && !populationsLoading && (
          // Career rows can't participate — no population exists for a null
          // season — so say so rather than leaving stale chips unexplained.
          <FooterText>
            Career rows keep their published percentiles
          </FooterText>
        )}
      </Stack>
    </>
  );
}

/** Season identity block. Both league and team are shown for either garden: a
 *  pro blob carries a player's pre-NBA seasons too, so the league varies down
 *  the column even on the NBA page. */
function buildIdentityColumns(): GridColDef[] {
  return [
    {
      field: "scope",
      headerName: "Season",
      width: 112,
      sortable: false,
      valueGetter: (_v, row: PlayerStatsScopeRow) => scopeLabel(row),
      renderCell: (params) => {
        const row = params.row as PlayerStatsScopeRow;
        const career = isCareer(row);
        return (
          <Typography
            sx={{
              fontSize: career ? 13 : 14.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: career ? "uppercase" : "none",
              color: career ? "primary.main" : "#17191c",
            }}
          >
            {scopeLabel(row)}
          </Typography>
        );
      },
    },
    {
      field: "league",
      headerName: "League",
      width: 104,
      valueGetter: (_v, row: PlayerStatsScopeRow) => row.league ?? "",
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.5} height="100%">
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#3f4650" }}>
            {(params.row as PlayerStatsScopeRow).league ?? "All"}
          </Typography>
          <DevSourceLabel
            source={(params.row as PlayerStatsScopeRow).source}
            sx={{ px: 0.5 }}
          />
        </Stack>
      ),
    },
    {
      field: "team",
      headerName: "Team",
      width: 118,
      valueGetter: (_v, row: PlayerStatsScopeRow) => row.team ?? "",
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#3f4650" }}>
          {(params.row as PlayerStatsScopeRow).team ?? "—"}
        </Typography>
      ),
    },
    {
      field: "gp",
      headerName: "GP",
      width: 58,
      type: "number",
      valueGetter: (_v, row: PlayerStatsScopeRow) => row.gp,
    },
    {
      field: "poss",
      headerName: "Poss",
      width: 78,
      type: "number",
      valueGetter: (_v, row: PlayerStatsScopeRow) => row.poss,
      valueFormatter: (value: number | null) =>
        typeof value === "number" ? value.toLocaleString("en-US") : "—",
    },
  ];
}

function FooterText({
  children,
  bold,
}: {
  children: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: bold ? 700 : 600,
        letterSpacing: bold ? "0.09em" : "0.04em",
        color: bold ? "#17191c" : "#8a8a8a",
      }}
    >
      {children}
    </Typography>
  );
}

export { CAREER_ROW_CLASS };
